import httpService from "../../services/httpService";
import { ApiEndpoints } from "../../constants/apiEndpoints";
import {
  AdminUser,
  AdminUserDevice,
  AdminUserNotificationDevice,
  AdminUserNotification,
  AdminUserRole,
  AdminUserSong,
} from "../../types/admin";
import { PaginationMeta } from "../../types/api";

/**
 * API responses in this backend are sometimes wrapped:
 *  - { success, message, data }
 * and sometimes returned as plain data.
 * This helper makes the client resilient to both formats.
 */
function unwrapData<T>(payload: any): T {
  if (payload && typeof payload === "object" && "data" in payload) {
    return payload.data as T;
  }
  return payload as T;
}

function collectionFrom<T>(payload: unknown, keys: string[] = ["items"]): T[] {
  if (Array.isArray(payload)) return payload as T[];
  if (!payload || typeof payload !== "object") return [];

  const record = payload as Record<string, unknown>;
  for (const key of keys) {
    if (Array.isArray(record[key])) return record[key] as T[];
  }

  return [];
}

function nestedMeta(payload: unknown): PaginationMeta | null {
  if (!payload || typeof payload !== "object") return null;
  const meta = (payload as Record<string, unknown>).meta;
  return meta && typeof meta === "object" ? (meta as PaginationMeta) : null;
}

// -------------------- Types --------------------

// User interface (aligned with backend User model)
export interface User {
  [key: string]: unknown;
  _id?: string; // MongoDB object ID (comes as _id)
  user_id: string; // Firebase UID

  email?: string;
  name?: string;

  display_name?: string;
  assigned_song_count?: number;
  device_count?: number;

  fcm_token?: string | null;

  created_at: string;
  last_login: string;

  assigned_song_ids: string[];
  assigned_songv2_ids?: string[]; // newer model support

  devices: Device[];

  is_deleted?: boolean;
  deleted_at?: string | null;
  status?: "active" | "disabled" | "pending";
  role?: string | null;
  roles?: string[];
  updated_at?: string;

  isAdmin?: boolean; // Admin yetkisi kontrolü için
}

export interface UsersPaginatedResponse {
  items: User[];
  meta: PaginationMeta | null;
  users?: User[];
  page?: number;
  page_size?: number;
  total_returned?: number;
}

export interface Device {
  device_id: string; // UUID
  model: string;
  hardware_version?: string;
  firmware_version?: string;
  serial_number: string;
  is_active: boolean;
  paired_at: string;
  last_connected_at: string;
}

// Request interfaces
export interface UpdateProfileRequest {
  name?: string;
  display_name?: string;
  email?: string;
}

export interface AddDeviceRequest {
  model: string;
  serial_number: string;
  hardware_version?: string;
  firmware_version?: string;
  is_active?: boolean;
}

export interface UpdateDeviceRequest {
  model?: string;
  hardware_version?: string;
  firmware_version?: string;
  is_active?: boolean;
  last_connected_at?: string;
}

export interface ApiSuccessResponse {
  success: boolean;
  message: string;
}

export interface ApiEnvelope<T> {
  success: boolean;
  message?: string;
  data: T;
  meta?: PaginationMeta | null;
}

export interface UpdateUserStatusRequest {
  status: "active" | "disabled";
}

export interface FetchUsersParams {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  role?: string;
  sort_by?: string;
  sort_order?: "asc" | "desc";
}

export interface UpdateUserRolesRequest {
  roles: string[];
}

// -------------------- API --------------------

function normalizeRoles(value: unknown): string[] {
  if (!Array.isArray(value)) return [];

  return value
    .map((role) => {
      if (typeof role === "string") return role;
      if (role && typeof role === "object") {
        const record = role as Record<string, unknown>;
        return record.role || record.name || record.value;
      }
      return "";
    })
    .filter((role): role is string => typeof role === "string" && Boolean(role));
}

function userPayload(payload: unknown): Record<string, unknown> {
  if (!payload || typeof payload !== "object") return {};
  const record = payload as Record<string, unknown>;
  if (record.user && typeof record.user === "object") {
    return record.user as Record<string, unknown>;
  }
  return record;
}

function userEndpoint(base: string, userId: string, suffix = ""): string {
  return `${base}/${encodeURIComponent(userId)}${suffix}`;
}

function mapAdminUser(item: AdminUser | Record<string, unknown>): User {
  const source = userPayload(item);
  const roles = normalizeRoles(source.roles);
  const userId = String(
    source.user_id ||
      source.uid ||
      source.firebase_uid ||
      source.id ||
      source._id ||
      ""
  );
  const status =
    source.status === "disabled" || source.status === "pending"
      ? source.status
      : "active";

  return {
    ...source,
    user_id: userId,
    name: typeof source.name === "string" ? source.name : undefined,
    display_name:
      typeof source.display_name === "string" ? source.display_name : undefined,
    email: typeof source.email === "string" ? source.email : undefined,
    assigned_song_count:
      typeof source.assigned_song_count === "number"
        ? source.assigned_song_count
        : 0,
    device_count:
      typeof source.device_count === "number" ? source.device_count : 0,
    created_at:
      typeof source.created_at === "string" ? source.created_at : "",
    updated_at:
      typeof source.updated_at === "string" ? source.updated_at : undefined,
    last_login:
      typeof source.last_login === "string" ? source.last_login : "",
    status,
    role: typeof source.role === "string" ? source.role : null,
    roles,
    assigned_song_ids: Array.isArray(source.assigned_song_ids)
      ? (source.assigned_song_ids as string[])
      : [],
    assigned_songv2_ids: Array.isArray(source.assigned_songv2_ids)
      ? (source.assigned_songv2_ids as string[])
      : [],
    devices: Array.isArray(source.devices) ? (source.devices as Device[]) : [],
    is_deleted: status === "disabled",
    isAdmin: roles.some((role) => role.toUpperCase() === "ADMIN"),
  };
}

// Keep filtering, sorting and pagination on the server so searches cover the
// complete user directory rather than only the currently loaded page.
export const fetchUsers = async (
  params: FetchUsersParams = {}
): Promise<UsersPaginatedResponse> => {
  const response = await httpService.get<AdminUser[] | { items?: AdminUser[] }>(
    ApiEndpoints.ADMIN_USERS,
    { params }
  );
  const items = collectionFrom<AdminUser>(response.data, ["items", "users"]).map(
    mapAdminUser
  );
  const meta = response.meta || nestedMeta(response.data);

  return {
    items,
    meta,
    users: items,
    page: Number(meta?.page || params.page || 1),
    page_size: Number(meta?.limit || params.limit || items.length),
    total_returned: Number(meta?.total || items.length),
  };
};

// Admin - Get user (GET /admin/users/{user_id})
export const fetchUser = async (user_id: string): Promise<User> => {
  const response = await httpService.get<AdminUser | { user?: AdminUser }>(
    userEndpoint(ApiEndpoints.ADMIN_USER_BY_ID, user_id)
  );
  return mapAdminUser(response.data);
};

// Admin - Create user (NOT implemented in backend by default)
export const addUser = async (_user: UpdateProfileRequest): Promise<User> => {
  // Keeping function signature to avoid breaking imports, but backend typically doesn't expose POST /users.
  throw new Error("POST /users is not available in the backend. Use /users/me (auto-create on login).");
};

// There is no admin user deletion operation in the current API contract.
export const deleteUser = async (_user_id: string): Promise<void> => {
  throw new Error(
    "Admin kullanıcı silme endpoint'i mevcut değil. Hesabı /status ile pasifleştirin."
  );
};

// Admin - Update user (PUT /admin/users/{user_id})
export const updateUser = async (user_id: string, user: UpdateProfileRequest): Promise<User> => {
  const response = await httpService.put<AdminUser | { user?: AdminUser }>(
    userEndpoint(ApiEndpoints.ADMIN_USER_BY_ID, user_id),
    user
  );
  return mapAdminUser(response.data);
};

export const updateUserStatus = async (
  user_id: string,
  payload: UpdateUserStatusRequest
): Promise<{ requires_token_refresh?: boolean }> => {
  const response = await httpService.put<any>(
    userEndpoint(ApiEndpoints.ADMIN_USER_STATUS, user_id, "/status"),
    payload
  );
  return {
    requires_token_refresh: response.meta?.requires_token_refresh === true,
  };
};

export interface AdminUserRolesResponse {
  user_id: string;
  roles: AdminUserRole[];
  admin: boolean;
  requires_token_refresh?: boolean;
}

export const fetchUserRoles = async (
  user_id: string
): Promise<AdminUserRolesResponse> => {
  const response = await httpService.get<
    unknown[] | {
      user_id?: string;
      items?: unknown[];
      roles?: unknown[];
      admin?: boolean;
      requires_token_refresh?: boolean;
    }
  >(userEndpoint(ApiEndpoints.ADMIN_USER_ROLES, user_id, "/roles"));
  const roleItems = collectionFrom<unknown>(response.data, ["items", "roles"])
    .map((item): AdminUserRole | null => {
      if (typeof item === "string") return { role: item };
      if (!item || typeof item !== "object") return null;
      const record = item as Record<string, unknown>;
      const role = record.role || record.name || record.value;
      if (typeof role !== "string" || !role) return null;
      return { ...record, role } as AdminUserRole;
    })
    .filter((item): item is AdminUserRole => item !== null);
  const record =
    response.data && !Array.isArray(response.data)
      ? response.data
      : {};

  return {
    user_id:
      typeof record.user_id === "string" ? record.user_id : user_id,
    roles: roleItems,
    admin:
      typeof record.admin === "boolean"
        ? record.admin
        : roleItems.some((item) => item.role.toUpperCase() === "ADMIN"),
    requires_token_refresh:
      typeof record.requires_token_refresh === "boolean"
        ? record.requires_token_refresh
        : undefined,
  };
};

export const updateUserRoles = async (
  user_id: string,
  payload: UpdateUserRolesRequest
): Promise<{ requires_token_refresh?: boolean }> => {
  const response = await httpService.put<any>(
    userEndpoint(ApiEndpoints.ADMIN_USER_ROLES, user_id, "/roles"),
    payload
  );
  return {
    requires_token_refresh: response.meta?.requires_token_refresh === true,
  };
};

export interface AdminUserDevicesResponse {
  devices: AdminUserDevice[];
  notification_devices: AdminUserNotificationDevice[];
}

export const fetchAdminUserDevices = async (
  user_id: string
): Promise<AdminUserDevicesResponse> => {
  const response = await httpService.get<
    | AdminUserDevice[]
    | {
        devices?: AdminUserDevice[];
        notification_devices?: AdminUserNotificationDevice[];
      }
  >(userEndpoint(ApiEndpoints.ADMIN_USER_DEVICES, user_id, "/devices"));

  if (Array.isArray(response.data)) {
    return { devices: response.data, notification_devices: [] };
  }

  return {
    devices: collectionFrom<AdminUserDevice>(response.data, ["devices"]),
    notification_devices: collectionFrom<AdminUserNotificationDevice>(
      response.data,
      ["notification_devices"]
    ),
  };
};

export const fetchAdminUserSongs = async (user_id: string): Promise<AdminUserSong[]> => {
  const response = await httpService.get<
    AdminUserSong[] | { items?: AdminUserSong[]; songs?: AdminUserSong[] }
  >(userEndpoint(ApiEndpoints.ADMIN_USER_SONGS, user_id, "/songs"));
  return collectionFrom<AdminUserSong>(response.data, ["items", "songs"]);
};

export interface UserNotificationsResponse {
  items: AdminUserNotification[];
  meta: PaginationMeta | null;
}

export const fetchAdminUserNotifications = async (
  user_id: string,
  page = 1,
  limit = 50
): Promise<UserNotificationsResponse> => {
  const response = await httpService.get<
    | AdminUserNotification[]
    | {
        items?: AdminUserNotification[];
        notifications?: AdminUserNotification[];
        meta?: PaginationMeta;
      }
  >(
    userEndpoint(ApiEndpoints.ADMIN_USER_NOTIFICATIONS, user_id, "/notifications"),
    { params: { page, limit } }
  );
  return {
    items: collectionFrom<AdminUserNotification>(response.data, [
      "items",
      "notifications",
    ]),
    meta: response.meta || nestedMeta(response.data),
  };
};

// Current user - Profile (GET /users/me)
export const fetchUserProfile = async (): Promise<User> => {
  const response = await httpService.get<any>(ApiEndpoints.USER_PROFILE);
  return unwrapData<User>(response.data);
};

// Current user - Update profile (PUT /users/me)
export const updateUserProfile = async (user: { name?: string }): Promise<User> => {
  const response = await httpService.put<any>(ApiEndpoints.USER_PROFILE, user);
  return unwrapData<User>(response.data);
};

// Current user - Update FCM token (PUT /users/me/fcm-token)
export const updateFcmToken = async (fcm_token: string): Promise<User> => {
  const response = await httpService.put<any>(ApiEndpoints.USER_FCM_TOKEN, { fcm_token });
  return unwrapData<User>(response.data);
};

// Current user - Assigned songs (GET /users/me/songs)
export const fetchAssignedSongs = async (): Promise<any[]> => {
  const response = await httpService.get<any>(ApiEndpoints.USER_SONGS);
  return response.data as any[];
};

// Current user - Assign V2 song to self.
export const assignSongToUser = async (
  _user_id: string,
  songv2_id: string
): Promise<ApiSuccessResponse> => {
  const res = await httpService.post<any>(ApiEndpoints.USER_SONGS, {
    songv2_id,
  });
  return {
    success: true,
    message: (res as any).message || "Song assignment completed",
  };
};

// Admin - Assign V2 song to a user.
export const adminAssignSongToUser = async (
  user_id: string,
  songv2_id: string,
  _currentSongIds: string[] = []
): Promise<ApiSuccessResponse> => {
  const response = await httpService.post<any>(
    `${ApiEndpoints.ADMIN_USERS}/${encodeURIComponent(user_id)}/songs`,
    { songv2_id }
  );
  return {
    success: true,
    message:
      (response.data as any)?.message ||
      "V2 şarkı kullanıcıya başarıyla atandı.",
  };
};

// Current user - Devices list (GET /users/me/devices)
export const fetchUserDevices = async (): Promise<Device[]> => {
  const response = await httpService.get<any>(ApiEndpoints.USER_DEVICES);
  return unwrapData<Device[]>(response.data);
};

// Current user - Add device (POST /users/me/devices)
export const addUserDevice = async (data: AddDeviceRequest): Promise<Device> => {
  const response = await httpService.post<any>(ApiEndpoints.USER_DEVICES, data);
  return unwrapData<Device>(response.data);
};

// Admin - Add device to a user (PUT /users/{user_id}/device)
export const adminAddDeviceToUser = async (
  _user_id: string,
  _data: AddDeviceRequest
): Promise<ApiSuccessResponse> => {
  throw new Error(
    "Admin kullanıcıya cihaz atama endpoint'i bulunmuyor. Bu işlem yalnızca kullanıcının /users/me/devices endpoint'i üzerinden yapılabilir."
  );
};

// Current user - Update device (PUT /users/me/devices/{device_id})
export const updateUserDevice = async (device_id: string, data: UpdateDeviceRequest): Promise<Device> => {
  const response = await httpService.put<any>(`${ApiEndpoints.USER_DEVICES}/${device_id}`, data);
  return unwrapData<Device>(response.data);
};

// Current user - Delete device (DELETE /users/me/devices/{device_id})
export const deleteUserDevice = async (device_id: string): Promise<void> => {
  await httpService.delete(`${ApiEndpoints.USER_DEVICES}/${device_id}`);
};

// Legacy e-mail based role helpers are intentionally blocked: the v1 API
// accepts a user ID and a complete roles array at /admin/users/{id}/roles.
export const setAdminClaim = async (_email: string): Promise<ApiSuccessResponse> => {
  throw new Error("Rolleri kullanıcı detayındaki rol yönetimi alanından güncelleyin.");
};

export const removeAdminClaim = async (_email: string): Promise<ApiSuccessResponse> => {
  throw new Error("Rolleri kullanıcı detayındaki rol yönetimi alanından güncelleyin.");
};

// Admin - Check if current user is admin (GET /api/admin/check-admin)
export const checkAdminStatus = async (): Promise<{ isAdmin: boolean; email?: string; uid?: string }> => {
  const response = await httpService.get<any>(ApiEndpoints.ADMIN_CHECK);
  return response.data as { isAdmin: boolean; email?: string; uid?: string };
};

// Admin - List all admin users (GET /api/admin/list-admins)
export const listAdminUsers = async (): Promise<{ uid: string; email: string; displayName?: string }[]> => {
  const response = await httpService.get<AdminUser[]>(ApiEndpoints.ADMIN_USERS, {
    params: { role: "ADMIN", limit: 200 },
  });
  return (response.data || []).map((u) => ({
    uid: u.user_id,
    email: u.email || "",
    displayName: u.name,
  }));
};
